// Formatação e contas de horário da tela Recuperar atendimentos.

const WEEKDAYS_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** "2026-10-15" -> "15/10" */
export function formatDayMonth(date) {
  if (!date) return '';
  const [, month, day] = String(date).split('-');
  return day && month ? `${day}/${month}` : date;
}

/** "2026-10-15" -> "15/10/2026" */
export function formatFullDate(date) {
  if (!date) return '';
  const [year, month, day] = String(date).split('-');
  return day && month && year ? `${day}/${month}/${year}` : date;
}

/** "2026-10-15" + "Quarta" -> "Qua 15/10" (o dia da semana vem da API; sem ele, calcula) */
export function formatDayLabel(date, dayName) {
  const short = dayName ? dayName.slice(0, 3) : WEEKDAYS_SHORT[new Date(`${date}T12:00:00`).getDay()];
  return `${short} ${formatDayMonth(date)}`;
}

/** "2026-10-15 14:00:00" -> "15/10 14:00" */
export function formatDateTime(datetime) {
  if (!datetime) return '';
  const [date, time] = String(datetime).split(' ');
  return `${formatDayMonth(date)}${time ? ` ${time.slice(0, 5)}` : ''}`;
}

/** 150 -> "R$ 150,00" */
export function formatMoney(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'R$ 0,00';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** "N pessoa(s)", "N horário(s)"... */
export function plural(count, singular, pluralForm) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** "14:30" -> 870 */
export function toMinutes(time) {
  if (!time) return 0;
  const [h, m] = String(time).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** 870 -> "14:30" */
export function toTime(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * Passo da agenda deduzido dos intervalos livres: todo início e toda duração são
 * múltiplos do passo configurado, então o máximo divisor comum deles é o passo
 * (ou um múltiplo dele). Fica entre 5 e 60 minutos; sem dados, 30.
 */
export function inferStepMinutes(days) {
  let step = 0;
  (days || []).forEach((day) => {
    (day.intervals || []).forEach((interval) => {
      step = gcd(step, toMinutes(interval.start));
      step = gcd(step, Number(interval.minutes) || 0);
    });
  });
  if (!step) return 30;
  return Math.min(60, Math.max(5, step));
}

const CLASSIFY_BATCH = 3;
const CLASSIFY_CONCURRENCY = 2;

/**
 * Pede a leitura das conversas das pessoas com `chat.pending`, em lotes de 3 e
 * no máximo 2 lotes ao mesmo tempo. `classify(ids, cb)` chama a API;
 * `onResult(patientId, chat)` entrega cada pessoa lida; `onDone(changed)` fecha
 * com true quando alguma intenção que muda a lista apareceu. Se a API responder
 * que a IA não está disponível (ou falhar), não pede mais nada. Devolve a função
 * que cancela o restante (troca de intervalo).
 */
export function runChatClassification(candidates, classify, onResult, onDone) {
  const ids = (candidates || []).filter((c) => c.chat?.pending).map((c) => c.patientId);
  const batches = [];
  for (let i = 0; i < ids.length; i += CLASSIFY_BATCH) batches.push(ids.slice(i, i + CLASSIFY_BATCH));

  let cancelled = false;
  let finished = false;
  let next = 0;
  let running = 0;
  let changed = false;

  function finish() {
    if (finished || cancelled) return;
    finished = true;
    onDone(changed);
  }

  function launch() {
    if (cancelled) return;
    if (next >= batches.length) {
      if (running === 0) finish();
      return;
    }
    const batch = batches[next];
    next += 1;
    running += 1;
    classify(batch, (response) => {
      running -= 1;
      if (cancelled) return;
      if (!response?.success || !response.data || response.data.available === false) {
        next = batches.length;
      } else {
        (response.data.results || []).forEach((item) => {
          if (item.chat?.intent && item.chat.intent !== 'neutral') changed = true;
          onResult(item.patientId, item.chat);
        });
      }
      launch();
    });
  }

  for (let i = 0; i < CLASSIFY_CONCURRENCY; i += 1) launch();
  return () => { cancelled = true; };
}

/**
 * Horários possíveis dentro do intervalo para um atendimento de `minutes`,
 * de `step` em `step`. O horário sugerido pela API entra mesmo fora do passo.
 */
export function buildTimeOptions(interval, minutes, step, suggested) {
  if (!interval) return [];
  const start = toMinutes(interval.start);
  const end = toMinutes(interval.end);
  const duration = Math.max(Number(minutes) || 0, 0);
  const options = [];
  for (let t = start; t + duration <= end; t += step) options.push(t);
  if (suggested) {
    const s = toMinutes(suggested);
    if (s >= start && s + duration <= end && !options.includes(s)) options.push(s);
  }
  return options.sort((a, b) => a - b).map(toTime);
}

/** Abre a conversa no WhatsApp Web (dígitos com DDI), para conferir o que a IA leu. */
export function whatsappWebUrl(digits) {
  return digits ? `https://web.whatsapp.com/send?phone=${digits}` : '';
}

const CHAT_INTENT_TEXT = {
  declined: 'não vai continuar',
  postponed: 'adiou',
  ignored: 'não respondeu à equipe',
  wants_other_time: 'pediu horário',
  interested: 'mostrou interesse',
  neutral: 'sem sinal',
};

/** "Leitura da IA (mostrou interesse): <resumo>", para conferir a interpretação; '' sem leitura. */
export function chatReading(chat) {
  if (!chat || chat.pending || !chat.summary) return '';
  const intent = CHAT_INTENT_TEXT[chat.intent];
  return `Leitura da IA${intent ? ` (${intent})` : ''}: ${chat.summary}`;
}
